import {BarChart3} from 'lucide-react'
import type { Unit, LiquidType, LyeType, LyeCalculationMode, SoapCalculations } from "@/data/oilDatabase"
import { MoldVolumeCalculator } from "@/components/MoldVolumeCalculator"
import type { MoldDimensions, MoldPreset } from "@/hooks/useMoldPresets"

interface RecipeSettingsProps {
  unit: Unit
  setUnit: (v: Unit) => void
  liquidType: LiquidType
  setLiquidType: (v: LiquidType) => void
  lyeType: LyeType
  setLyeType: (v: LyeType) => void
  superfat: number
  setSuperfat: (v: number) => void
  waterPercent: number
  setWaterPercent: (v: number) => void
  lyeCalculationMode: LyeCalculationMode
  setLyeCalculationMode: (v: LyeCalculationMode) => void
  lyeConcentration: number
  setLyeConcentration: (v: number) => void
  batchSize: number
  setBatchSize: (v: number) => void
  fragrancePercent: number
  setFragrancePercent: (v: number) => void
  calculations: SoapCalculations
  moldPresets: MoldPreset[]
  moldPresetsLoading: boolean
  moldPresetsSaving: boolean
  canSaveMoldPresets: boolean
  onSaveMoldPreset: (dimensions: MoldDimensions) => Promise<boolean>
  onDeleteMoldPreset: (id: string) => Promise<void>
  onRequestSignIn: () => void
}

function ToggleButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`py-2 px-3 rounded-lg font-medium transition-all text-sm ${
        active
          ? "bg-emerald-600 text-white shadow"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      {children}
    </button>
  )
}

export function RecipeSettings({
  unit, setUnit,
  liquidType, setLiquidType,
  lyeType, setLyeType,
  superfat, setSuperfat,
  waterPercent, setWaterPercent,
  lyeCalculationMode, setLyeCalculationMode,
  lyeConcentration, setLyeConcentration,
  batchSize, setBatchSize,
  fragrancePercent, setFragrancePercent,
  calculations,
  moldPresets,
  moldPresetsLoading,
  moldPresetsSaving,
  canSaveMoldPresets,
  onSaveMoldPreset,
  onDeleteMoldPreset,
  onRequestSignIn,
}: RecipeSettingsProps) {
  return (
    <aside className="lg:col-span-3 space-y-4">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 size={20} />
          Recipe Settings
        </h2>

        {/* Batch Size */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Batch Size (oils only)
          </label>
          <input
            type="number"
            value={batchSize}
            onChange={(e) => setBatchSize(Number(e.target.value))}
            className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">{unit}</p>
        </div>

        {/* Unit Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Unit</label>
          <div className="grid grid-cols-2 gap-2">
            <ToggleButton active={unit === "grams"} onClick={() => setUnit("grams")}>Grams</ToggleButton>
            <ToggleButton active={unit === "ounces"} onClick={() => setUnit("ounces")}>Ounces</ToggleButton>
          </div>
        </div>

        {/* Lye Type */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Lye Type</label>
          <div className="grid grid-cols-2 gap-2">
            <ToggleButton active={lyeType === "NaOH"} onClick={() => setLyeType("NaOH")}>NaOH</ToggleButton>
            <ToggleButton active={lyeType === "KOH"} onClick={() => setLyeType("KOH")}>KOH</ToggleButton>
          </div>
        </div>

        {/* Superfat Percentage */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Superfat %</label>
          <input
            type="number"
            value={superfat}
            onChange={(e) => setSuperfat(Number(e.target.value))}
            className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            min="0"
            max="20"
          />
        </div>

        {/* Fragrance Percentage */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Fragrance %</label>
          <input
            type="number"
            value={fragrancePercent}
            onChange={(e) => setFragrancePercent(Number(e.target.value))}
            className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            min="0"
            max="10"
            step="0.1"
          />
        </div>

        {/* Lye Calculation Mode */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Liquid Calculation</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setLyeCalculationMode("ratio")}
              className={`py-2 px-3 rounded-lg font-medium transition-all text-xs ${
                lyeCalculationMode === "ratio"
                  ? "bg-emerald-600 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Water Ratio
            </button>
            <button
              onClick={() => setLyeCalculationMode("concentration")}
              className={`py-2 px-3 rounded-lg font-medium transition-all text-xs ${
                lyeCalculationMode === "concentration"
                  ? "bg-emerald-600 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Lye Conc.
            </button>
          </div>

          {lyeCalculationMode === "ratio" ? (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Water as % of Oils</label>
              <input
                type="number"
                value={waterPercent}
                onChange={(e) => setWaterPercent(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                min="0"
                max="100"
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lye Concentration %</label>
              <input
                type="number"
                value={lyeConcentration}
                onChange={(e) => setLyeConcentration(Number(e.target.value))}
                className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                min="25"
                max="50"
              />
            </div>
          )}
        </div>

        {/* Liquid Type */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Liquid Type</label>
          <select
            value={liquidType}
            onChange={(e) => setLiquidType(e.target.value as LiquidType)}
            className="w-full py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="water">Water</option>
            <option value="goat-milk">Goat Milk</option>
            <option value="aloe-juice">Aloe Juice</option>
          </select>
        </div>

        {/* Results Summary */}
        {calculations.totalOilWeight > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Results</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Lye:</span>
                <span className="font-semibold text-gray-900">
                  {calculations.totalLyeRequired.toFixed(1)} {unit}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Liquid:</span>
                <span className="font-semibold text-gray-900">
                  {calculations.totalLiquid.toFixed(1)} {unit}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fragrance:</span>
                <span className="font-semibold text-gray-900">
                  {calculations.fragranceLoad.toFixed(1)} {unit}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-900 font-semibold">Total:</span>
                <span className="font-bold text-emerald-600">
                  {calculations.totalBatchWeight.toFixed(1)} {unit}
                </span>
              </div>
              {calculations.totalCost > 0 && (
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-900 font-semibold">Cost:</span>
                  <span className="font-bold text-gray-900">
                    ${calculations.totalCost.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mold Volume Calculator */}
      <MoldVolumeCalculator
        unit={unit}
        setBatchSize={setBatchSize}
        presets={moldPresets}
        presetsLoading={moldPresetsLoading}
        presetsSaving={moldPresetsSaving}
        canSavePresets={canSaveMoldPresets}
        onSavePreset={onSaveMoldPreset}
        onDeletePreset={onDeleteMoldPreset}
        onRequestSignIn={onRequestSignIn}
      />
    </aside>
  )
}
